import { ConfigProvider, notification } from 'antd';
import csCZ from 'antd/lib/locale/cs_CZ';
import enUS from 'antd/lib/locale/en_US';
import dayjs from 'dayjs';
import React, { Dispatch, SetStateAction, createContext, useState } from 'react';
import { IAmlz } from 'types/amlz';
import { ELeiPlatba, ILeiContact, ILeiData, ILeiDetailData, ILeiPrevod } from 'types/leiTypes';
import { IPrehledy } from 'types/spravceUctuType';

import NotificationWrapper, { INotification } from 'components/atoms/NotificationComponent';

import { IIdentifikaceEmitenta } from 'services/akcionar/emise/identifikaceEmitenta';
import { IcisMeny } from 'services/cisMeny';
import { IcisStaty } from 'services/cisStaty';
import { IDokumentyJinaPrehled, ILikvidacePrikaz, IVypisJina } from 'services/dokumenty/prikazy';
import { IDokument, IDokumentyPrava, IDokumentyPrehled } from 'services/dokumenty/smlouva';
import { IEmitent } from 'services/emitent';
import { ILeiInfo } from 'services/lei/PrehledUctu';
import { ILeiLoginLessData, IRozpZadostData } from 'services/lei/leiLoginLess';
import { IPlatbaLogin } from 'services/lei/leiPlatba';
import { IZalozeniLeiResp } from 'services/lei/zalozeniLei';
import { IPristupy, IProfily } from 'services/profily';
import { IDetailDLTinput, IPozadavkyPolozka } from 'services/specialniSluzby/uePozadavky';
import { IPrehledPozadavek } from 'services/specialniSluzby/uePrehled';
import { IDataZaevidovaniGet } from 'services/specialniSluzby/ueZaevidovani';
import { IFilterDVP, IPozadavkyDVP } from 'services/specialniSluzby/uevDVP';
import { IZadostPravoResp } from 'services/spravaEmitenta/zadostPravo';
import { IUctyUdalosti } from 'services/spravceUctu/spravceHlasovani';

import { ELeiStavZadosti } from 'enums/lei/leiStavZadosti';
import { ETypZadosti } from 'enums/lei/leiTypZadosti';
import { ETypSeznam } from 'enums/specialniSluzby/typSeznam';
import { EUpozorneni } from 'enums/statniSprava/upozorneni';

// Sem vkladat jednotlive casti store, napr. token, language, nactena data z API ktera budou globalni pro vsechny
export interface ILanguageObject {
    [key: string]: string;
}
export interface IDokmentyContext {
    jinaOsobaZadostData?: IVypisJina | ILikvidacePrikaz;
    selectedZadost?: IDokument;
    prehledData?: IDokumentyPrehled | IDokumentyJinaPrehled;
    goToSignatures?: boolean;
    dokumentyPrava?: IDokumentyPrava;
    dokumentyMenuReady?: boolean;
}

export interface IStore {
    token: string | null;
    tokenURL: string;
    dashboardLoading: boolean;
    leiLogin: ILeiLogin | null;
    language: 'cz' | 'en';
    profily: IProfily | null;
    aktivniFirma: IPristupy | null; // Nastaveni aktivni firmy hlavne pro volani api
    ucty: IPristupy[] | null; // Veskere ucty klienta, prace v AppMenu a na jednotlivych kartach
    showIsinEdit: boolean; // nastavuje sa v prehlade isinu pri nacitani ziadosti
    isin: IIdentifikaceEmitenta | null;
    srdHlasovani: IUctyUdalosti[] | null;
    idSRD: number | null;
    isinSRD?: string | null;
    amlz: IAmlz | null;
    lei: ILei | null;
    prevPath: string; // setnute v dashboardWrapper pri unmounte
    menuProfile: IEmitent | null; // profil podla ktoreho sa zobrazi horne menu v dashboardwrape, sluzba sa vola v prehlede
    dashboardWrapperTopSelected: string[] | string | null;
    cisStaty: IcisStaty[] | [];
    cisMeny: IcisMeny[] | [];
    NotificationComponent: (props: INotification) => void;
    specialniSluzbyDVP: {
        detailDVP?: IPozadavkyDVP;
        detailFiltrDVP?: IFilterDVP | null;
        seznamTyp?: ETypSeznam | null;
        pozadavek?: IPozadavkyPolozka;
    } | null;
    specialniSluzby: {
        detail?: IPrehledPozadavek;
        pozadavek?: IPozadavkyPolozka;
        seznamTyp?: ETypSeznam | null;
        updateDetailu?: boolean | null;
        detailFiltr?: IDataZaevidovaniGet | null;
        detailFiltrDLT?: IDetailDLTinput | null;
    } | null;
    platbaLei: IPlatbaLogin | ELeiPlatba | null;
    zadostAgenda: IZadostPravoResp | null;
    editaceTextu: {
        id: string;
        cz: ILanguageObject;
        en: ILanguageObject;
    } | null;
    serviceMode: boolean;
    logoutTimestamp: dayjs.Dayjs | null;
    spravceSelectedCard: IPrehledy | null;
    mePozadavkyBisign: ImePozadavkyBisign | null;
    dokumenty: IDokmentyContext | null;
}

export interface ImePozadavkyBisign {
    sluzba?: string;
    requestoid?: number;
    datum?: string;
    upozor?: {
        typ: EUpozorneni;
    }[];
}

export interface ILei {
    data?: ILeiData; // pouzite pri novej / rozpracovanej ziadosti
    datumPodani?: string;
    stavZadosti?: ELeiStavZadosti;
    idVarsymZadosti?: IZalozeniLeiResp;
    selectedLei?: ILeiInfo;
    detailData?: ILeiDetailData; // pouzite pri aktualizacii a plat instrukcech
    prevodData?: ILeiPrevod;
    contactData?: ILeiContact;
    paymentDueDate?: string;
    loginless?: {
        typzadosti: ETypZadosti;
        spolecnost?: ILeiLoginLessData;
        osoba?: IRozpZadostData;
    };
}

export interface ILeiLogin {
    mail?: string;
    lei?: string;
    leimigraceid?: string;
}

// Dipsatcher pro store, dle zadani bude upravovan
interface IStoreActions {
    setStoreState?: Dispatch<SetStateAction<IStore>>;
}

export type IStoreKeys = keyof IStore;

// defaultni hodnota globalniho store, prozatim {}
export const defaultStoreState: IStore = {
    token: null,
    leiLogin: null,
    tokenURL: '',
    language: 'cz',
    dashboardLoading: false,
    profily: null,
    aktivniFirma: null,
    ucty: [],
    isin: null,
    idSRD: null,
    srdHlasovani: null,
    amlz: null,
    lei: null,
    prevPath: '',
    menuProfile: null,
    showIsinEdit: false,
    dashboardWrapperTopSelected: null,
    cisStaty: [],
    cisMeny: [],
    NotificationComponent: () => {},
    specialniSluzbyDVP: null,
    specialniSluzby: null,
    platbaLei: null,
    zadostAgenda: null,
    editaceTextu: null,
    serviceMode: false,
    logoutTimestamp: null,
    spravceSelectedCard: null,
    mePozadavkyBisign: null,
    dokumenty: null
};

// globalni store
export const Context = createContext<IStore & IStoreActions>(defaultStoreState);

interface Props {
    children?: React.ReactNode;
}

// Zakladni komponenta se store, ktera nam dovoluje pouzivat v jejich childech useContext
const Store: React.FC<Props> = ({ children }) => {
    const [storeState, setStoreState] = useState<IStore>(defaultStoreState);
    // notification component musime declarovat tu aby mal pristup ku current state (Robilo neplechu pri zmene jazyka)
    const [api, contextHolder] = notification.useNotification();
    const NotificationComponent = NotificationWrapper(api);

    return (
        <ConfigProvider
            locale={storeState.language === 'cz' ? csCZ : enUS}
            theme={{
                token: {
                    colorPrimary: '#007777',
                    colorInfo: '#007777',
                    colorLink: '#007777',
                    colorInfoActive: '#007777',
                    colorInfoBorder: '#007777',
                    colorInfoText: '#007777',
                    colorInfoTextActive: '#007777',
                    colorLinkActive: '#007777',
                    colorLinkHover: '#007777',
                    colorPrimaryActive: '#007777',
                    colorPrimaryBorder: '#007777',
                    colorPrimaryHover: '#007777',
                    colorPrimaryText: '#007777',
                    colorPrimaryTextActive: '#007777',
                    fontSize: 16
                }
            }}
        >
            <Context.Provider
                value={{
                    ...storeState,
                    NotificationComponent,
                    setStoreState
                }}
            >
                {contextHolder}
                {children}
            </Context.Provider>{' '}
        </ConfigProvider>
    );
};

export default Store;
